import React from "react";

// Dane i hooki: SERVICES[], DIFFS[], ModalCtx, useReveal, Counter, useMeta
const SERVICES = [
  { slug: "google-ads", k: "01", name: "Google Ads", desc: "Takie, które się opłaca. Od Search po YouTube.", meta: "Search · Performance Max · Shopping" },
  { slug: "meta-ads", k: "02", name: "Meta Ads", desc: "Facebook, Instagram, Advantage+. Kreacje w cenie usługi.", meta: "Shopping · Retargeting" },
  { slug: "analityka", k: "03", name: "Analityka GA4 + GTM", desc: "Poprawny i czysty tracking w sklepach, dashboardy w 14 dni.", meta: "GA4 · GTM · Looker" },
  { slug: "capi", k: "04", name: "Conversions API", desc: "Server-side CAPI. Odzyskaj dane, które gubi iOS.", meta: "CAPI · GTM Server" },
  { slug: "feed", k: "05", name: "Optymalizacja feedu", desc: "Custom labels, parametry, labeling. Zwiększ widoczność i konwersje.", meta: "Channable · GMC" },
  { slug: "marketing-dla-ecommerce", k: "06", name: "Marketing dla e-commerce", desc: "Cały marketing, analityka i wsparcie biznesowe w jednym miejscu.", meta: "Full-stack · CMO" },
  { slug: "allegro", k: "07", name: "Allegro Ads", desc: "70% polskiego ecommerce. Kampanie sponsorowane, optymalizacja ofert i kont.", meta: "Ads · Smart · SEO" },
];

const DIFFS = [
  { k: "01", title: "Rok zobowiązania vs wypowiedzenie 24h", sup: "Typowa agencja wiąże Cię umową na minimum rok. U nas — bez umowy, miesiąc do miesiąca. Wypowiedzenie 24h, do końca okresu rozliczeniowego." },
  { k: "02", title: "Junior army vs senior studio", sup: "W dużych agencjach trafiasz do juniora, który uczy się na Twoim budżecie. U nas do doświadczonego specjalisty (min. 5 lat)." },
  { k: "03", title: "Miesięczny raport vs dashboard 24/7", sup: "Większość agencji pokazuje liczby raz na miesiąc w PDF-ie. My — w czasie rzeczywistym. Skupione na realnym zysku, a nie tylko ROAS." },
  { k: "04", title: "Przepłacasz za zbędną pracę ludzi", sup: "Gdzie indziej płacisz za masę roboczogodzin. My nie boimy się technologii i automatyzacji. A cena to tylko jedna z ich zalet." },
];

// Cal.com modal context
const ModalCtx = React.createContext({ open: () => {}, close: () => {} });

// Animated counter (0 → target)
const Counter = ({ to, suffix = "", prefix = "", duration = 1400, decimals = 0, sep = " " }) => {
  const ref = React.useRef(null);
  const [val, setVal] = React.useState(0);
  const started = React.useRef(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(to * eased);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [to, duration]);
  const formatted = (() => {
    const v = decimals ? val.toFixed(decimals) : Math.round(val).toString();
    const [int, dec] = v.split(".");
    const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return dec ? `${withSep},${dec}` : withSep;
  })();
  return <span ref={ref}>{prefix}{formatted}{suffix}</span>;
};

// Reveal observer
const useReveal = (ref) => {
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const nodes = el.querySelectorAll(".rv, .hl-paint");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("on"); });
    }, { threshold: 0.15 });
    nodes.forEach(n => obs.observe(n));
    // Fallback: reveal anything already visible on mount
    const reveal = () => {
      nodes.forEach(n => {
        const r = n.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) n.classList.add("on");
      });
    };
    requestAnimationFrame(reveal);
    return () => obs.disconnect();
  }, [ref]);
};

// Wymuszenie reveal w trybie edytora (Puck): treść od razu widoczna, bez czekania na
// scroll/IntersectionObserver. Brak dep array → ponawiane co render (elementy domontowane
// po edycji pola też zostają ujawnione). Na publicznej stronie (editor=false) no-op.
const useEditorReveal = (ref, editor) => {
  React.useEffect(() => {
    if (!editor) return;
    const el = ref.current;
    if (el) el.querySelectorAll(".rv, .hl-paint").forEach((n) => n.classList.add("on"));
  });
};

// Per-route meta hook — sets <title>, description, canonical, og:*, twitter:*.
// Call once at top of each page component; effect re-runs only when title/description change.
const SITE_URL = "https://letscommerce.pl";
const DEFAULT_OG_IMAGE = SITE_URL + "/assets/logo-light.png";
const useMeta = ({ title, description, robots }) => {
  React.useEffect(() => {
    const path = window.location.pathname || "/";
    const canonical = SITE_URL + (path === "/" ? "/" : path.replace(/\/$/, ""));
    const setMeta = (key, attrName, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attrName}='${key}']`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    const setLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel='${rel}']`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };
    if (title) document.title = title;
    setMeta("description", "name", description);
    setMeta("robots", "name", robots); // opcjonalne: "noindex,nofollow" dla stron niepublicznych
    setMeta("og:title", "property", title);
    setMeta("og:description", "property", description);
    setMeta("og:url", "property", canonical);
    setMeta("og:image", "property", DEFAULT_OG_IMAGE);
    setMeta("twitter:title", "name", title);
    setMeta("twitter:description", "name", description);
    setMeta("twitter:image", "name", DEFAULT_OG_IMAGE);
    setLink("canonical", canonical);

    // SPA pageview — GTM auto-fires on initial load (gtm.js event), so skip first.
    // Subsequent route changes need a manual push otherwise GA4/Pixel see only homepage.
    window.dataLayer = window.dataLayer || [];
    if (window.__lcFirstView) {
      window.dataLayer.push({
        event: "page_view",
        page_path: path,
        page_title: title || document.title,
        page_location: canonical
      });
    } else {
      window.__lcFirstView = true;
    }
  }, [title, description, robots]);
};

// Helper: builds {href, onClick} props for SPA navigation that ALSO behaves as
// a native browser link (Cmd+click → new tab, middle-click → new tab, right-click
// → "Open in new tab" context menu, scrapers see real href, etc.).
//
//   const link = makeNavLink(navigate);
//   <a {...link("/uslugi")}>Usługi</a>
//
// Modifier-key clicks bail out of preventDefault → browser handles them natively.
const makeNavLink = (navigate, onAfter) => (path) => ({
  href: path,
  onClick: (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button && e.button !== 0)) return;
    e.preventDefault();
    navigate(path);
    if (onAfter) onAfter();
  },
});

export { SERVICES, DIFFS, ModalCtx, Counter, useReveal, useEditorReveal, useMeta, makeNavLink };
